/**
 * MiAgendaModal.jsx - Unified agenda hub "Mi Agenda"
 *
 * Fullscreen modal showing all reminders from:
 * - Object reminders (recordatoriosObjetos)
 * - User-level personal reminders
 * - Google Calendar events (if connected)
 *
 * Kanban-style grouping: Esta semana, Proxima semana, Proximo mes, Mas adelante, Vencidos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  obtenerAgenda,
  crearRecordatorioPersonal,
  actualizarRecordatorioPersonal,
  eliminarRecordatorioPersonal,
  analizarImagenConIA,
} from '../../services/api/modules/agenda';

// ============= CONSTANTS =============

const COLORS = {
  green: '#84cc16',
  greenDark: '#65a30d',
  purple: '#9C27B0',
  purpleLight: '#F3E5F5',
  googleBlue: '#4285F4',
  googleBlueBg: '#F5F5F5',
  danger: '#ef4444',
  white: '#ffffff',
};

const FRECUENCIAS = [
  { value: 'no', label: 'No se repite' },
  { value: 'diario', label: 'Diario' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'anual', label: 'Anual' },
];

const TIPO_EMOJIS = {
  mascota: '🐶',
  llavero: '🔑',
  tarjeta: '💳',
  persona: '👤',
  objeto: '🔑',
};

// ============= HELPERS =============

function getWeekBounds() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // Start of this week (Monday)
  const startOfWeek = new Date(now);
  const day = startOfWeek.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  startOfWeek.setDate(startOfWeek.getDate() + diff);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(endOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  const startNextWeek = new Date(endOfWeek);
  startNextWeek.setDate(startNextWeek.getDate() + 1);
  startNextWeek.setHours(0, 0, 0, 0);

  const endNextWeek = new Date(startNextWeek);
  endNextWeek.setDate(endNextWeek.getDate() + 6);
  endNextWeek.setHours(23, 59, 59, 999);

  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  return { now, startOfWeek, endOfWeek, startNextWeek, endNextWeek, endOfMonth };
}

function classifyItem(item) {
  const { now, endOfWeek, endNextWeek, endOfMonth } = getWeekBounds();
  const fecha = item.fecha ? new Date(item.fecha + (item.fecha.includes('T') ? '' : 'T00:00:00')) : null;

  if (!fecha) return 'masAdelante';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (fecha < today) return 'vencidos';
  if (fecha <= endOfWeek) return 'estaSemana';
  if (fecha <= endNextWeek) return 'proximaSemana';
  if (fecha <= endOfMonth) return 'proximoMes';
  return 'masAdelante';
}

function formatDate(fechaStr) {
  if (!fechaStr) return '';
  const d = new Date(fechaStr + (fechaStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function formatTime(hora) {
  if (!hora) return '';
  const [h, m] = hora.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function formatMonto(monto, moneda) {
  if (!monto) return null;
  return `$${Number(monto).toLocaleString('es-MX')} ${moneda || 'MXN'}`;
}

function normalizeFechaSimple(fecha) {
  if (!fecha) return null;
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
  // DD/MM/YYYY or DD-MM-YYYY
  const match = fecha.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Try parsing as Date
  const d = new Date(fecha);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return null;
}

function normalizeHoraSimple(hora) {
  if (!hora) return null;
  // Already HH:MM
  if (/^\d{2}:\d{2}$/.test(hora)) return hora;
  // H:MM or HH:MM with AM/PM
  const match = hora.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (match) {
    let [, h, m, period] = match;
    h = parseInt(h, 10);
    if (period) {
      if (period.toUpperCase() === 'PM' && h < 12) h += 12;
      if (period.toUpperCase() === 'AM' && h === 12) h = 0;
    }
    return `${String(h).padStart(2, '0')}:${m}`;
  }
  return null;
}

// ============= SUB-COMPONENTS =============

function AgendaCard({ item, onEdit, onDelete }) {
  const isObjeto = item.source === 'objeto';
  const isPersonal = item.source === 'personal';
  const isGoogle = item.source === 'google';

  const borderColor = isObjeto ? COLORS.green : isPersonal ? COLORS.purple : COLORS.googleBlue;
  const bgColor = isGoogle ? COLORS.googleBlueBg : COLORS.white;

  const emoji = isObjeto ? (TIPO_EMOJIS[item.objetoTipo] || '🔑') : null;
  const montoStr = formatMonto(item.monto, item.moneda);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-3)',
        marginBottom: 'var(--space-2)',
        backgroundColor: bgColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-900)' }}>
            {isGoogle ? '📆' : '📅'} {item.title}
          </span>
          {montoStr && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', fontWeight: 'var(--font-weight-medium)' }}>
              {montoStr}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-1)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)' }}>
            {formatDate(item.fecha)}
            {item.hora ? ` ${formatTime(item.hora)}` : ''}
            {isGoogle && item.start && !item.hora ? ` ${new Date(item.start).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </div>
      </div>

      {/* Badge de origen */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
        {isObjeto && (
          <span style={{
            fontSize: 'var(--font-size-xs)',
            backgroundColor: 'var(--gray-100)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--gray-700)',
            whiteSpace: 'nowrap',
          }}>
            {emoji} {item.objetoNombre}
          </span>
        )}
        {isPersonal && (
          <span style={{
            fontSize: 'var(--font-size-xs)',
            backgroundColor: COLORS.purpleLight,
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            color: COLORS.purple,
            fontWeight: 'var(--font-weight-medium)',
          }}>
            Personal
          </span>
        )}
        {isGoogle && (
          <span style={{
            fontSize: 'var(--font-size-xs)',
            backgroundColor: '#E8F0FE',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            color: COLORS.googleBlue,
            fontWeight: 'var(--font-weight-medium)',
          }}>
            Google
          </span>
        )}

        {/* Action buttons */}
        {item.canEdit && (
          <button
            onClick={() => onEdit(item)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}
            title="Editar"
          >
            <i className="bi bi-pencil"></i>
          </button>
        )}
        {item.canDelete && (
          <button
            onClick={() => onDelete(item)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: COLORS.danger, fontSize: 'var(--font-size-sm)' }}
            title="Eliminar"
          >
            <i className="bi bi-trash"></i>
          </button>
        )}
        {isGoogle && item.htmlLink && (
          <a
            href={item.htmlLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.googleBlue, fontSize: 'var(--font-size-sm)', padding: '4px' }}
            title="Abrir en Google Calendar"
          >
            <i className="bi bi-box-arrow-up-right"></i>
          </a>
        )}
      </div>
    </div>
  );
}

function KanbanGroup({ label, items, expanded, onToggle, onEdit, onDelete }) {
  if (!items || items.length === 0) return null;

  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: 'var(--space-2) 0',
          userSelect: 'none',
        }}
      >
        <span style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--gray-800)',
        }}>
          {label} ({items.length})
        </span>
        <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} style={{ color: 'var(--gray-500)' }}></i>
      </div>

      {expanded && (
        <div>
          {items.map((item) => (
            <AgendaCard
              key={`${item.source}-${item.id}`}
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============= TIME PICKER =============

const HORAS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTOS_5 = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

function hora24ToComponents(hora24) {
  if (!hora24) return { hour: 9, minute: '00', period: 'AM' };
  const [h, m] = hora24.split(':').map(Number);
  const minute = String(Math.round(m / 5) * 5 % 60).padStart(2, '0');
  let hour = h % 12;
  if (hour === 0) hour = 12;
  const period = h >= 12 ? 'PM' : 'AM';
  return { hour, minute, period };
}

function componentsToHora24(hour, minute, period) {
  let h = hour;
  if (period === 'PM' && hour !== 12) h = hour + 12;
  if (period === 'AM' && hour === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${minute}`;
}

function TimePicker({ value, onChange, style }) {
  const { hour, minute, period } = hora24ToComponents(value);

  const selectStyle = {
    padding: 'var(--space-2) var(--space-1)',
    border: '1px solid var(--gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    outline: 'none',
    minWidth: '52px',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 6px center',
    paddingRight: '22px',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', ...style }}>
      <select
        value={hour}
        onChange={e => onChange(componentsToHora24(Number(e.target.value), minute, period))}
        style={selectStyle}
      >
        {HORAS_12.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--gray-600)' }}>:</span>
      <select
        value={minute}
        onChange={e => onChange(componentsToHora24(hour, e.target.value, period))}
        style={selectStyle}
      >
        {MINUTOS_5.map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={e => onChange(componentsToHora24(hour, minute, e.target.value))}
        style={{ ...selectStyle, minWidth: '56px' }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

// ============= PERSONAL REMINDER FORM =============

function RecordatorioPersonalForm({ onSubmit, onCancel, editData }) {
  const [que, setQue] = useState(editData?.que || editData?.title || '');
  const [fecha, setFecha] = useState(editData?.fecha || '');
  const [hora, setHora] = useState(editData?.hora || '');
  const [repetir, setRepetir] = useState(editData?.repetir || 'no');
  const [notas, setNotas] = useState(editData?.notas || '');
  const [monto, setMonto] = useState(editData?.monto || '');
  const [moneda, setMoneda] = useState(editData?.moneda || 'MXN');
  const [esPago, setEsPago] = useState(!!(editData?.monto && editData.monto > 0));
  const [guardando, setGuardando] = useState(false);

  // Image AI extraction state
  const [imagePreview, setImagePreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [extractionDone, setExtractionDone] = useState(false);
  const fileInputRef = React.useRef(null);

  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  })();

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máximo 10MB)');
      return;
    }

    setExtractionError(null);
    setExtractionDone(false);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target?.result;
      setImagePreview(base64);
      await handleExtractFromImage(base64);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleExtractFromImage = async (base64) => {
    setExtracting(true);
    setExtractionError(null);

    try {
      const result = await analizarImagenConIA(base64);

      if (result.error) {
        setExtractionError(result.error);
        return;
      }

      // Pre-fill form with extracted data
      if (result.que) setQue(result.que);
      if (result.fecha) {
        // Normalize date to YYYY-MM-DD
        const normalized = normalizeFechaSimple(result.fecha);
        if (normalized) setFecha(normalized);
      }
      if (result.hora) {
        const normalized = normalizeHoraSimple(result.hora);
        if (normalized) setHora(normalized);
      }
      if (result.repetir && FRECUENCIAS.some(f => f.value === result.repetir)) {
        setRepetir(result.repetir);
      }
      if (result.notas) setNotas(result.notas);

      // Extract monto from detalles if available
      const montoVal = result.detalles?.monto || result.detalles?.pago_monto;
      if (montoVal && Number(montoVal) > 0) {
        setEsPago(true);
        setMonto(String(montoVal));
        if (result.detalles?.moneda) setMoneda(result.detalles.moneda);
      }

      setExtractionDone(true);
      toast.success('Datos extraídos de la imagen');
    } catch (err) {
      console.error('Error extracting from image:', err);
      setExtractionError('Error al analizar la imagen. Intenta de nuevo.');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!que.trim() || !fecha) return;

    setGuardando(true);
    try {
      await onSubmit({
        que: que.trim(),
        fecha,
        hora: hora || null,
        repetir,
        notas: notas.trim() || null,
        monto: monto ? parseFloat(monto) : null,
        moneda: monto ? moneda : null,
      });
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--space-2) var(--space-3)',
    border: '1px solid var(--gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--gray-700)',
    marginBottom: 'var(--space-1)',
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: 'var(--space-4)' }}>
      <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-4)', color: 'var(--gray-900)' }}>
        {editData ? 'Editar recordatorio' : 'Nuevo recordatorio personal'}
      </h3>

      {/* Photo AI extraction button (only for new reminders) */}
      {!editData && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          {!imagePreview && !extracting && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                border: '2px dashed var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--gray-50, #f9fafb)',
                color: 'var(--gray-600)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-lg)' }}>📷</span>
              Crear desde foto
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                — La IA extrae los datos
              </span>
            </button>
          )}

          {extracting && (
            <div style={{
              width: '100%',
              padding: 'var(--space-3)',
              border: '2px solid var(--gray-200)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--gray-50, #f9fafb)',
              textAlign: 'center',
            }}>
              <div className="spinner-border spinner-border-sm" style={{ color: COLORS.purple }} role="status" />
              <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 0 }}>
                Analizando imagen con IA...
              </p>
            </div>
          )}

          {imagePreview && !extracting && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-2)',
              border: `1px solid ${extractionDone ? COLORS.green : extractionError ? COLORS.danger : 'var(--gray-200)'}`,
              borderRadius: 'var(--radius-md)',
              backgroundColor: extractionDone ? '#f0fdf4' : extractionError ? '#fef2f2' : 'var(--gray-50, #f9fafb)',
            }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: '48px',
                  height: '48px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-sm)',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                  {extractionDone ? '✓ Datos extraídos — revisa y ajusta el formulario' : extractionError || 'Imagen cargada'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setImagePreview(null); setExtractionDone(false); setExtractionError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 'var(--font-size-base)', padding: '4px', flexShrink: 0 }}
                title="Quitar imagen"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}

          {extractionError && !imagePreview && (
            <div style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: '#fef2f2',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: COLORS.danger,
            }}>
              <i className="bi bi-exclamation-triangle me-1"></i>
              {extractionError}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 'var(--space-3)' }}>
        <label style={labelStyle}>
          ¿Para qué es tu recordatorio? *
        </label>
        <input
          type="text"
          value={que}
          onChange={e => setQue(e.target.value)}
          placeholder="Ej. Clase de chino, Pago de luz..."
          maxLength={200}
          required
          style={inputStyle}
          autoFocus
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <div>
          <label style={labelStyle}>¿Cuándo? *</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} min={editData ? undefined : minDate} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Hora</label>
          <TimePicker value={hora || '09:00'} onChange={setHora} />
        </div>
      </div>

      <div style={{ marginBottom: 'var(--space-3)' }}>
        <label style={labelStyle}>¿Se repite?</label>
        <select value={repetir} onChange={e => setRepetir(e.target.value)} style={inputStyle}>
          {FRECUENCIAS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Monto toggle + fields */}
      {!esPago ? (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={() => setEsPago(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--gray-500)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
          >
            💰 ¿Es un pago? Agregar monto
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Monto</label>
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0.00" step="0.01" min="0" style={inputStyle} />
          </div>
          <div style={{ width: '90px' }}>
            <label style={labelStyle}>Moneda</label>
            <select value={moneda} onChange={e => setMoneda(e.target.value)} style={inputStyle}>
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => { setEsPago(false); setMonto(''); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--gray-400)',
              fontSize: 'var(--font-size-base)',
              padding: '8px 4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            title="Quitar monto"
          >
            ✕
          </button>
        </div>
      )}

      <div style={{ marginBottom: 'var(--space-4)' }}>
        <label style={labelStyle}>Notas (opcional)</label>
        <textarea
          value={notas}
          onChange={e => setNotas(e.target.value)}
          placeholder="Notas adicionales..."
          maxLength={500}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            border: '1px solid var(--gray-300)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--white)',
            color: 'var(--gray-700)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={guardando || !que.trim() || !fecha}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            backgroundColor: guardando ? 'var(--gray-400)' : COLORS.green,
            color: 'var(--gray-900)',
            cursor: guardando ? 'not-allowed' : 'pointer',
            fontWeight: 'var(--font-weight-semibold)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {guardando ? 'Guardando...' : (editData ? 'Guardar cambios' : 'Crear recordatorio')}
        </button>
      </div>
    </form>
  );
}

// ============= MAIN COMPONENT =============

export default function MiAgendaModal({ show, onClose, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEnabled, setCalendarEnabled] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    estaSemana: true,
    proximaSemana: false,
    proximoMes: false,
    masAdelante: false,
    vencidos: false,
  });

  const fetchAgenda = useCallback(async () => {
    setLoading(true);
    try {
      const result = await obtenerAgenda();
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setItems(result.data || []);
      setCalendarConnected(result.calendarConnected || false);
    } catch (err) {
      console.error('Error fetching agenda:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchAgenda();
    }
  }, [show, fetchAgenda]);

  // Group items by kanban bucket
  const filteredItems = calendarEnabled ? items : items.filter(i => i.source !== 'google');

  const groups = {
    estaSemana: [],
    proximaSemana: [],
    proximoMes: [],
    masAdelante: [],
    vencidos: [],
  };

  for (const item of filteredItems) {
    const bucket = classifyItem(item);
    groups[bucket].push(item);
  }

  // Sort within each group by date
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => {
      const da = a.fecha ? new Date(a.fecha) : new Date('2099-01-01');
      const db2 = b.fecha ? new Date(b.fecha) : new Date('2099-01-01');
      return da - db2;
    });
  }

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // CRUD handlers
  const handleCreate = async (data) => {
    const result = await crearRecordatorioPersonal(data);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Recordatorio creado');
    setShowForm(false);
    fetchAgenda();
    if (onCountChange) onCountChange();
  };

  const handleEdit = (item) => {
    if (item.source === 'personal') {
      setEditItem(item);
      setShowForm(true);
    } else if (item.source === 'objeto') {
      // Object reminders are edited from their own QuickReminder view
      toast.info('Edita este recordatorio desde la vista del objeto');
    }
  };

  const handleUpdate = async (data) => {
    if (!editItem) return;
    const result = await actualizarRecordatorioPersonal(editItem.id, data);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success('Recordatorio actualizado');
    setShowForm(false);
    setEditItem(null);
    fetchAgenda();
    if (onCountChange) onCountChange();
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    if (confirmDelete.source === 'personal') {
      const result = await eliminarRecordatorioPersonal(confirmDelete.id);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      toast.success('Recordatorio eliminado');
    } else if (confirmDelete.source === 'objeto') {
      toast.info('Elimina este recordatorio desde la vista del objeto');
    }

    setConfirmDelete(null);
    fetchAgenda();
    if (onCountChange) onCountChange();
  };

  if (!show) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: COLORS.white,
        display: 'flex',
        flexDirection: 'column',
        overscrollBehavior: 'none',
      }}
    >
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.greenDark} 0%, ${COLORS.green} 100%)`,
        padding: 'var(--space-3) var(--space-4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <h2 style={{
          margin: 0,
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--gray-900)',
        }}>
          📅 Mi agenda
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <button
            onClick={() => { setEditItem(null); setShowForm(true); }}
            style={{
              padding: 'var(--space-1) var(--space-3)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'var(--gray-900)',
              cursor: 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
          >
            <i className="bi bi-plus-lg"></i>
            Recordatorio
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--gray-900)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xl)',
              padding: 'var(--space-1)',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Google Calendar toggle */}
      {calendarConnected && (
        <div style={{
          padding: 'var(--space-2) var(--space-4)',
          borderBottom: '1px solid var(--gray-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            📆 Google Calendar
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <label style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px' }}>
              <input
                type="checkbox"
                checked={calendarEnabled}
                onChange={() => setCalendarEnabled(!calendarEnabled)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: calendarEnabled ? COLORS.green : 'var(--gray-300)',
                borderRadius: '22px', transition: '0.3s',
              }}>
                <span style={{
                  position: 'absolute', height: '16px', width: '16px', left: calendarEnabled ? '21px' : '3px',
                  bottom: '3px', backgroundColor: COLORS.white, borderRadius: '50%', transition: '0.3s',
                }} />
              </span>
            </label>
            <button
              onClick={fetchAgenda}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}
              title="Recargar"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 'var(--space-3) var(--space-4)',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
      }}>
        {showForm ? (
          <RecordatorioPersonalForm
            onSubmit={editItem ? handleUpdate : handleCreate}
            onCancel={() => { setShowForm(false); setEditItem(null); }}
            editData={editItem}
          />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--gray-500)' }}>
            <div className="spinner-border spinner-border-sm" style={{ color: COLORS.green }} role="status" />
            <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>Cargando agenda...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--gray-500)' }}>
            <i className="bi bi-calendar-x" style={{ fontSize: '3rem', marginBottom: 'var(--space-3)', display: 'block' }}></i>
            <p style={{ fontSize: 'var(--font-size-base)' }}>No tienes recordatorios</p>
            <button
              onClick={() => { setEditItem(null); setShowForm(true); }}
              style={{
                marginTop: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-4)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: COLORS.green,
                color: 'var(--gray-900)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-semibold)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <i className="bi bi-plus-lg me-1"></i>
              Crear recordatorio
            </button>
          </div>
        ) : (
          <>
            <KanbanGroup
              label="Esta semana"
              items={groups.estaSemana}
              expanded={expandedGroups.estaSemana}
              onToggle={() => toggleGroup('estaSemana')}
              onEdit={handleEdit}
              onDelete={setConfirmDelete}
            />
            <KanbanGroup
              label="Próxima semana"
              items={groups.proximaSemana}
              expanded={expandedGroups.proximaSemana}
              onToggle={() => toggleGroup('proximaSemana')}
              onEdit={handleEdit}
              onDelete={setConfirmDelete}
            />
            <KanbanGroup
              label="Próximo mes"
              items={groups.proximoMes}
              expanded={expandedGroups.proximoMes}
              onToggle={() => toggleGroup('proximoMes')}
              onEdit={handleEdit}
              onDelete={setConfirmDelete}
            />
            <KanbanGroup
              label="Más adelante"
              items={groups.masAdelante}
              expanded={expandedGroups.masAdelante}
              onToggle={() => toggleGroup('masAdelante')}
              onEdit={handleEdit}
              onDelete={setConfirmDelete}
            />
            <KanbanGroup
              label="Vencidos"
              items={groups.vencidos}
              expanded={expandedGroups.vencidos}
              onToggle={() => toggleGroup('vencidos')}
              onEdit={handleEdit}
              onDelete={setConfirmDelete}
            />
          </>
        )}
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)',
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: COLORS.white,
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              maxWidth: '400px',
              width: '100%',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h4 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--font-size-base)' }}>
              ¿Eliminar recordatorio?
            </h4>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', margin: '0 0 var(--space-4) 0' }}>
              "{confirmDelete.title}" será eliminado permanentemente.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: COLORS.white,
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: COLORS.danger,
                  color: COLORS.white,
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
